<script setup>
import { reactive, onMounted, ref, computed } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'
import { dataHojeBR } from '@/utils/useDataBR'
import { CNT_CMB_ST } from '@/constants/CNT_CMB_ST'
import { criarConfigBuscaLimiteCredito } from '@/search-definitions/limiteCreditoSearch'
import { criarConfigBuscaTituloOriginal } from '@/search-definitions/tituloFinanceiroBuscaSearch'

const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  modeloVazio: {
    type: Object,
    default: () => ({
      idContratoCambio: 0,
      idInstituicaoFinanceira: null,
      nomeInstituicaoFinanceira: '',
      idLimiteCredito: null,
      numeroContrato: '',
      lstTipoOperacaoCambio: '',
      lstStatusCambio: CNT_CMB_ST.EmDigitacao,
      dataFechamento: dataHojeBR(),
      dataVencimento: dataHojeBR(),
      lstMoedaEstrangeira: '',
      valorMoedaEstrangeira: null,
      taxaCambialFechada: null,
      observacoes: '',
      titulosLiquidados: []
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const mostrarAuditoria = ref(false)

const accordionState = reactive({
  identificacao: true,
  condicoes: true,
  valores: true,
  liquidacoes: true
})

const listas = reactive({})

const liquidacaoEmEdicao = ref(null)
const showModalLiquidacao = computed({
  get: () => !!liquidacaoEmEdicao.value,
  set: (val) => { if (!val) liquidacaoEmEdicao.value = null }
})

const podeEditar = computed(() => {
  return authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('CAMBIO_COMPLETO')) || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO'))
})

const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

const configBuscaBanco = computed(() => ({
  ...parceiroSearch,
  titulo: 'Buscar Instituição Financeira',
  filtrosFixos: {
    isInstFinanceira: true,
    idEmpresa: authStore.empresaAtiva
  }
}))

const isContratado = computed(() => item.value.lstStatusCambio === CNT_CMB_ST.Contratado)
const isEmDigitacao = computed(() => item.value.lstStatusCambio === CNT_CMB_ST.EmDigitacao || !item.value.lstStatusCambio)
const podeEditarCampos = computed(() => podeEditar.value && isEmDigitacao.value)
const isContratadoReadOnly = computed(() => isEditing.value && isContratado.value)

const configBuscaLimiteCredito = computed(() =>
  criarConfigBuscaLimiteCredito(authStore.organizacaoAtiva, item.value.idInstituicaoFinanceira)
)

function getTextoLista (lista, id) {
  if (!lista) return id || '-'
  const found = lista.find(l => String(l.idListaClienteItem) === String(id))
  return found ? found.textoLista : (id || '-')
}

const statusTexto = computed(() => {
  return getTextoLista(listas.LstStatusCambio, item.value.lstStatusCambio)
})

const isLoadingAcao = ref(false)
const confirmModal = ref(null)
const mensagemConfirmacao = ref('Tem certeza que deseja realizar esta ação?')

const podeCancelar = computed(() =>
  isEditing.value &&
  (isEmDigitacao.value || isContratado.value) &&
  podeEditar.value
)

const confirmarCancelar = () => {
  mensagemConfirmacao.value = `Tem certeza que deseja cancelar o contrato ${item.value.numeroContrato}? Esta ação não poderá ser desfeita.`
  confirmModal.value.show()
}

const cancelarContrato = async () => {
  if (isLoadingAcao.value) return
  isLoadingAcao.value = true
  try {
    const res = await api.post(`/ContratosCambio/${item.value.idContratoCambio}/cancelar`)
    Object.assign(item.value, res.data)
    emit('show-notification', { message: 'Contrato cancelado com sucesso!', type: 'success' })
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao cancelar contrato.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingAcao.value = false
  }
}

const onConfirmAcao = async (confirmado) => {
  if (confirmado) {
    await cancelarContrato()
  }
}

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['ContratoCambio'])
    Object.assign(listas, result)
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar listas.', type: 'error' })
  }
}

const contratarContrato = async () => {
  if (!isEditing.value || !podeEditar.value) return
  try {
    const res = await api.post(`/ContratosCambio/${item.value.idContratoCambio}/contratar`)
    Object.assign(item.value, res.data)
    emit('show-notification', { message: 'Contrato contratado com sucesso!', type: 'success' })
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao contratar contrato.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

function iniciarNovaLiquidacao () {
  liquidacaoEmEdicao.value = {
    _isNew: true,
    idTituloFinanceiro: null,
    valorAplicadoMoedaEstrangeira: 0
  }
}

function salvarLiquidacaoLocal () {
  const liq = liquidacaoEmEdicao.value
  if (liq.valorAplicadoMoedaEstrangeira <= 0) {
    emit('show-notification', { message: 'Informe um valor aplicado válido.', type: 'warning' })
    return
  }

  const payload = {
    idContratoCambio: item.value.idContratoCambio,
    idTituloFinanceiro: liq.idTituloFinanceiro,
    valorAplicadoMoedaEstrangeira: liq.valorAplicadoMoedaEstrangeira
  }

  api.post('/CambioLiquidacoes', payload)
    .then(() => {
      emit('show-notification', { message: 'Título vinculado com sucesso!', type: 'success' })
      liquidacaoEmEdicao.value = null
      carregarLiquidacoes()
    })
    .catch(e => {
      const msg = e.response?.data?.message || 'Erro ao vincular título.'
      emit('show-notification', { message: msg, type: 'error' })
    })
}

const carregarLiquidacoes = async () => {
  if (!item.value.idContratoCambio) {
    item.value.titulosLiquidados = []
    return
  }
  try {
    const res = await api.get('/CambioLiquidacoes', {
      params: { idContratoCambio: item.value.idContratoCambio, pageSize: 999 }
    })
    item.value.titulosLiquidados = res.data || []
  } catch (e) {
    console.warn('Erro ao carregar liquidações:', e)
    item.value.titulosLiquidados = []
  }
}

const excluirLiquidacao = async (idCambioLiquidacao) => {
  try {
    await api.delete(`/CambioLiquidacoes/${idCambioLiquidacao}`)
    emit('show-notification', { message: 'Título desvinculado com sucesso!', type: 'success' })
    await carregarLiquidacoes()
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao desvincular título.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

async function salvar () {
  if (!podeEditar.value) return

  const totalLiquidado = item.value.titulosLiquidados.reduce((acc, curr) => acc + curr.valorAplicadoMoedaEstrangeira, 0)
  if (totalLiquidado > item.value.valorMoedaEstrangeira) {
    emit('show-notification', { message: 'A soma das liquidações excede o valor total do contrato.', type: 'warning' })
    return
  }

  const payload = {
    ...item.value
  }

  await submitForm('ContratosCambio', 'idContratoCambio', payload)
}

onMounted(async () => {
  await carregarListas()
  if (item.value.idContratoCambio) {
    await carregarLiquidacoes()
  }
})
</script>

<template>
  <FormularioCadastro
    module-name="Fechamento de Câmbio"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    :titulo-registro="item.numeroContrato"
    :status="statusTexto"
    @submit="salvar"
    @cancelar="handleCancel"
    @show-notification="(p) => emit('show-notification', p)"
  >
    <template #acoes-header v-if="isEditing">
      <button type="button" class="btn-icon-minimalista me-3" @click="mostrarAuditoria = true">
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <!-- BARRA DE AÇÕES DO CONTRATO DE CÂMBIO -->
    <div v-if="isEditing" class="d-flex justify-content-between align-items-center gap-2 mb-3">
      <div class="d-flex align-items-center gap-2">
        <button
          v-if="podeCancelar"
          type="button"
          class="btn btn-sm btn-outline-danger fw-bold"
          :disabled="isLoadingAcao"
          @click="confirmarCancelar"
        >
          <i class="bi bi-x-lg me-1"></i>
          Cancelar Contrato
        </button>
      </div>
      <div class="d-flex align-items-center gap-2">
        <button
          v-if="isEmDigitacao"
          type="button"
          class="btn btn-sm btn-success fw-bold"
          :disabled="isLoadingAcao"
          @click="contratarContrato"
        >
          <i class="bi bi-check-circle me-1"></i>
          Contrato Fechado
        </button>
      </div>
    </div>

    <div class="row g-3">
      <!-- SEÇÃO 1: Dados do Contrato -->
      <div class="col-12">
        <div class="card border-secondary-subtle">
          <div
            class="card-header cursor-pointer d-flex justify-content-between align-items-center"
            @click="accordionState.identificacao = !accordionState.identificacao"
          >
            <h6 class="mb-0"><i class="bi bi-file-earmark-text me-2"></i>Identificação do Contrato</h6>
            <i :class="['bi', accordionState.identificacao ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
          </div>
          <div class="card-body" v-show="accordionState.identificacao">
            <div class="row g-3">
              <div class="col-md-6">
                <CampoBusca
                  id="idInstituicaoFinanceira"
                  label="Instituição Financeira"
                  v-model="item.idInstituicaoFinanceira"
                  :configuracao-busca="configBuscaBanco"
                  required
                  :disabled="isContratadoReadOnly || !podeEditar"
                />
              </div>
              <div class="col-md-6">
                <CampoInput
                  id="numeroContrato"
                  label="Número do Contrato"
                  v-model="item.numeroContrato"
                  placeholder="Ex: CAM-2024-001"
                  required
                  :disabled="isContratadoReadOnly || !podeEditar"
                />
              </div>
              <div class="col-md-6">
                <CampoBusca
                  id="idLimiteCredito"
                  label="Limite de Crédito Vinculado"
                  v-model="item.idLimiteCredito"
                  :configuracao-busca="configBuscaLimiteCredito"
                  :disabled="!podeEditarCampos"
                />
              </div>
              <div class="col-md-6">
                <CampoBuscaLista
                  id="lstTipoOperacaoCambio"
                  label="Tipo de Operação"
                  v-model="item.lstTipoOperacaoCambio"
                  tabela="ContratoCambio"
                  required
                  :disabled="isContratadoReadOnly || !podeEditar"
                />
              </div>
              <div class="col-md-6">
                <CampoBuscaLista
                  id="lstStatusCambio"
                  label="Status do Contrato"
                  v-model="item.lstStatusCambio"
                  tabela="ContratoCambio"
                  contexto="Cliente"
                  disabled
                />
              </div>
              <div class="col-12">
                <CampoInput
                  id="observacoes"
                  label="Observações"
                  v-model="item.observacoes"
                  type="textarea"
                  rows="2"
                  :disabled="!podeEditar"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SEÇÃO 2: Condições e Datas -->
      <div class="col-md-4">
        <div class="card border-secondary-subtle">
          <div
            class="card-header cursor-pointer d-flex justify-content-between align-items-center"
            @click="accordionState.condicoes = !accordionState.condicoes"
          >
            <h6 class="mb-0"><i class="bi bi-calendar-event me-2"></i>Prazos</h6>
            <i :class="['bi', accordionState.condicoes ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
          </div>
          <div class="card-body" v-show="accordionState.condicoes">
            <div class="row g-3">
              <div class="col-md-6">
                <CampoInput
                  id="dataFechamento"
                  label="Data de Fechamento"
                  v-model="item.dataFechamento"
                  type="date"
                  required
                  :disabled="isContratadoReadOnly || !podeEditar"
                />
              </div>
              <div class="col-md-6">
                <CampoInput
                  id="dataVencimento"
                  label="Data de Vencimento"
                  v-model="item.dataVencimento"
                  type="date"
                  required
                  :disabled="isContratadoReadOnly || !podeEditar"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SEÇÃO 3: Valores e Taxas -->
      <div class="col-md-8">
        <div class="card border-secondary-subtle">
          <div
            class="card-header cursor-pointer d-flex justify-content-between align-items-center"
            @click="accordionState.valores = !accordionState.valores"
          >
            <h6 class="mb-0"><i class="bi bi-currency-dollar me-2"></i>Valores e Taxas</h6>
            <i :class="['bi', accordionState.valores ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
          </div>
          <div class="card-body" v-show="accordionState.valores">
            <div class="row g-3">
              <div class="col-md-4">
                <CampoBuscaLista
                  id="lstMoedaEstrangeira"
                  label="Moeda"
                  v-model="item.lstMoedaEstrangeira"
                  tabela="ContratoCambio"
                  required
                  :disabled="!podeEditarCampos"
                />
              </div>
              <div class="col-md-4">
                <CampoInput
                  id="valorMoedaEstrangeira"
                  label="Valor Estrangeiro"
                  v-model="item.valorMoedaEstrangeira"
                  type="moeda"
                  required
                  :disabled="isContratadoReadOnly || !podeEditar"
                />
              </div>
              <div class="col-4">
                <CampoInput
                  id="taxaCambialFechada"
                  label="Taxa de Câmbio Contratada"
                  v-model="item.taxaCambialFechada"
                  type="decimal"
                  :casas-decimais="4"
                  required
                  :disabled="!podeEditarCampos"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SEÇÃO 4: Vincular Títulos (apenas quando Contratado) -->
      <div class="col-12" v-if="isContratado">
        <div class="card border-primary-subtle">
          <div
            class="card-header bg-primary-subtle d-flex justify-content-between align-items-center cursor-pointer"
            @click="accordionState.liquidacoes = !accordionState.liquidacoes"
          >
            <h6 class="mb-0 text-primary-emphasis"><i class="bi bi-list-check me-2"></i>Vincular Títulos</h6>
            <div class="d-flex align-items-center">
              <button v-if="podeEditar" type="button" class="btn btn-sm btn-primary py-0 me-3" @click.stop="iniciarNovaLiquidacao">
                <i class="bi bi-plus-lg me-1"></i> Vincular Título
              </button>
              <i :class="['bi', accordionState.liquidacoes ? 'bi-chevron-up' : 'bi-chevron-down', 'text-primary']"></i>
            </div>
          </div>
          <div class="card-body p-0" v-show="accordionState.liquidacoes">
            <div class="table-responsive">
              <table class="table table-hover align-middle mb-0 liquidacoes-table">
                <thead class="small">
                  <tr>
                    <th>Fatura / Título</th>
                    <th class="text-end">Valor Aplicado</th>
                    <th class="text-end" style="width: 100px;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(liq, index) in item.titulosLiquidados" :key="index">
                    <td>
                      <span class="badge bg-info-subtle text-info-emphasis">Fatura: {{ liq.numeroFaturaOriginal || liq.idTituloFinanceiro }}</span>
                    </td>
                    <td class="text-end fw-bold">{{ liq.valorAplicadoMoedaEstrangeira.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }}</td>
                    <td class="text-end">
                      <button v-if="podeEditar" type="button" class="btn btn-link text-danger p-0" @click="excluirLiquidacao(liq.idCambioLiquidacao)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!item.titulosLiquidados || item.titulosLiquidados.length === 0">
                    <td colspan="3" class="text-center py-4 text-muted small">
                      Nenhuma liquidação vinculada. Este câmbio pode ser aplicado posteriormente na baixa.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12" v-else-if="isEditing">
        <div class="alert alert-info">
          <i class="bi bi-info-circle me-2"></i>
          O contrato precisa estar "Contratado" para vincular títulos financeiros.
        </div>
      </div>

    </div>
  </FormularioCadastro>

  <ModalFormulario v-model="showModalLiquidacao" titulo="Vincular Título" tamanho="md" @confirmar="salvarLiquidacaoLocal">
    <div v-if="liquidacaoEmEdicao" class="row g-3">
      <div class="col-12">
        <CampoBusca
          id="liqTitulo"
          label="Título Financeiro"
          v-model="liquidacaoEmEdicao.idTituloFinanceiro"
          :configuracao-busca="criarConfigBuscaTituloOriginal(authStore.organizacaoAtiva)"
          required
        />
      </div>
      <div class="col-12">
        <CampoInput id="liqValor" label="Valor a Liquidar (Estrangeiro)" v-model="liquidacaoEmEdicao.valorAplicadoMoedaEstrangeira" type="moeda" required />
      </div>
    </div>
  </ModalFormulario>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="ContratoCambio"
    :chave-primaria="item.idContratoCambio"
    :titulo-registro="item.numeroContrato"
    contexto="tenant"
    @fechar="mostrarAuditoria = false"
  />

  <ConfirmationModal
    ref="confirmModal"
    titulo="Cancelar Contrato de Câmbio"
    :mensagem="mensagemConfirmacao"
    botao-confirmar="Confirmar Cancelamento"
    botao-cancelar="Manter Contrato"
    tipo="danger"
    @confirm="onConfirmAcao"
  />
</template>

<style scoped>
.liquidacoes-table thead th {
  background-color: var(--bs-secondary-bg);
  color: var(--bs-body-color);
}
</style>
